import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LenisProvider, ScrollProgress, GMNav, HGNav, GMFooter, HGFooter, StickyTrialCTA, TrialPopup } from "@/components/layout";
import { ChatWidget } from "@/components/ChatWidget";
import { captureAttribution } from "@/lib/attribution";

captureAttribution();
import Home from "@/pages/gm/Home";
import About from "@/pages/gm/About";
import ProgramPage, { ProgramsIndex } from "@/pages/gm/Programs";
import LocationPage, { LocationsIndex } from "@/pages/gm/Locations";
import { SchedulePage, MembershipPage, CoachesPage, FAQsPage, ContactPage, WaiverPage, BlogPage } from "@/pages/gm/misc";
import CoachPage from "@/pages/gm/Coach";
import StaffPage from "@/pages/gm/Staff";
import BlogPost from "@/pages/gm/BlogPost";
import NearPage from "@/pages/gm/Near";
import { KeiserGiraoPage, QuotePage, NewsletterPage, StorePage, StoreHolmesMurphyPage, NotFoundPage } from "@/pages/gm/Legacy";
import { HGHub, HGLocation, HGKids, HGFreeTrial, HGMembership } from "@/pages/hg";

// 301 map from old Squarespace slugs (client-side; mirror in server/CDN config at deploy — see /app/docs/301-redirect-map.md)
const REDIRECTS = {
  "/kickboxing": "/programs/muay-thai-kickboxing",
  "/cid-tolkien": "/programs/private-lessons",
  "/jonathan-miller": "/programs/wrestling",
  "/audrey-drew": "/coaches/audrey-wolfe",
  "/guy-mezger": "/coaches/guy-mezger",
  "/david-douglas": "/coaches/david-douglas",
  "/coaches/keiser-girao": "/coaches",
  "/instructors": "/coaches",
  "/index": "/programs",
  "/classes": "/programs",
  "/home": "/",
  "/home-1": "/",
  "/class-schedule-1": "/schedule",
  "/class-schedule": "/schedule",
  "/title": "/",
  "/read-me-montauk": "/",
  "/join": "/membership",
  "/muay-thai": "/programs/muay-thai-kickboxing",
  "/boxing": "/programs/boxing",
  "/brazilian-jiu-jitsu": "/programs/brazilian-jiu-jitsu",
  "/wrestling": "/programs/wrestling",
  "/kids": "/programs/kids-martial-arts",
  "/lion-cubs": "/programs/lion-cubs",
  "/private-lessons": "/programs/private-lessons",
};

const GMShell = ({ children }) => {
  const staff = useLocation().pathname.startsWith("/staff");
  return (
    <>
      <GMNav />
      <main id="main">{children}</main>
      <GMFooter />
      {!staff && <StickyTrialCTA />}
      {!staff && <TrialPopup />}
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </>
  );
};

const HGShell = ({ children }) => (
  <>
    <HGNav />
    <main id="main">{children}</main>
    <HGFooter />
  </>
);

const AppRoutes = () => {
  const { pathname, search } = useLocation();
  if (REDIRECTS[pathname]) return <Navigate to={REDIRECTS[pathname] + search} replace />;
  return (
    <Routes>
      <Route path="/" element={<GMShell><Home /></GMShell>} />
      <Route path="/about" element={<GMShell><About /></GMShell>} />
      <Route path="/programs" element={<GMShell><ProgramsIndex /></GMShell>} />
      <Route path="/programs/:slug" element={<GMShell><ProgramPage /></GMShell>} />
      <Route path="/schedule" element={<GMShell><SchedulePage /></GMShell>} />
      <Route path="/membership" element={<GMShell><MembershipPage /></GMShell>} />
      <Route path="/coaches" element={<GMShell><CoachesPage /></GMShell>} />
      <Route path="/coaches/:slug" element={<GMShell><CoachPage /></GMShell>} />
      <Route path="/staff" element={<GMShell><StaffPage /></GMShell>} />
      <Route path="/locations" element={<GMShell><LocationsIndex /></GMShell>} />
      <Route path="/locations/:slug" element={<GMShell><LocationPage /></GMShell>} />
      <Route path="/near/:slug" element={<GMShell><NearPage /></GMShell>} />
      <Route path="/faqs" element={<GMShell><FAQsPage /></GMShell>} />
      <Route path="/blog" element={<GMShell><BlogPage /></GMShell>} />
      <Route path="/blog/:slug" element={<GMShell><BlogPost /></GMShell>} />
      <Route path="/contact" element={<GMShell><ContactPage /></GMShell>} />
      <Route path="/waiver" element={<GMShell><WaiverPage /></GMShell>} />

      {/* Legacy pages preserved from the original guymezger.com (see docs/guymezger-migration-readiness.md) */}
      <Route path="/keiser-girao" element={<GMShell><KeiserGiraoPage /></GMShell>} />
      <Route path="/quote" element={<GMShell><QuotePage /></GMShell>} />
      <Route path="/newsletter" element={<GMShell><NewsletterPage /></GMShell>} />
      <Route path="/store" element={<GMShell><StorePage /></GMShell>} />
      <Route path="/store/holmesmurphy" element={<GMShell><StoreHolmesMurphyPage /></GMShell>} />

      <Route path="/hiddengym" element={<Navigate to="/hiddengym/martial-arts" replace />} />
      <Route path="/hiddengym/martial-arts" element={<HGShell><HGHub /></HGShell>} />
      <Route path="/hiddengym/martial-arts/kids" element={<HGShell><HGKids /></HGShell>} />
      <Route path="/hiddengym/martial-arts/free-trial" element={<HGShell><HGFreeTrial /></HGShell>} />
      <Route path="/hiddengym/martial-arts/:slug" element={<HGShell><HGLocation /></HGShell>} />
      <Route path="/hiddengym/membership" element={<HGShell><HGMembership /></HGShell>} />

      {/* Branded 404 (noindex). NOTE: SPA hosting returns HTTP 200 for unknown URLs — the
          meta robots noindex on this page is the soft-404 mitigation; a true 404 status
          would require server-side routing the production stack doesn't support. */}
      <Route path="*" element={<GMShell><NotFoundPage /></GMShell>} />
    </Routes>
  );
};

const ChatWidgetGate = () => (useLocation().pathname.startsWith("/staff") ? null : <ChatWidget />);

function App() {
  return (
    <BrowserRouter>
      <LenisProvider>
        <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:bg-white focus:px-4 focus:py-2">Skip to content</a>
        <ScrollProgress />
        <ChatWidgetGate />
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </LenisProvider>
    </BrowserRouter>
  );
}

export default App;
